/**
 * 제휴사 등록/수정 폼 페이지
 */

import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Button, Form, Spinner, Row, Col } from 'react-bootstrap';
import AdminLayout from '../../components/layout/AdminLayout';
import { partnerService } from '../../services/partnerService';
import { Partner } from '../../types/partner';

interface PartnerFormData {
  partner_name: string;
  partner_code: string;
  contact_name?: string;
  email?: string;
  phone?: string;
  commission_rate: number;
  status: 'active' | 'inactive';
}

const PartnerForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const isNew = location.pathname.includes('/new');
  const partnerId = isNew ? 0 : (id ? parseInt(id, 10) : 0);
  const isEdit = !!partnerId && partnerId > 0;

  const { register, handleSubmit, formState: { errors }, reset } = useForm<PartnerFormData>({
    defaultValues: {
      status: 'active',
      commission_rate: 0,
    },
  });

  const { data: partner, isLoading } = useQuery({
    queryKey: ['partner', partnerId],
    queryFn: () => partnerService.getPartner(partnerId),
    enabled: isEdit,
  });

  useEffect(() => {
    if (partner && isEdit) {
      reset({
        partner_name: partner.partner_name,
        partner_code: partner.partner_code,
        contact_name: partner.contact_name || '',
        email: partner.email || '',
        phone: partner.phone || '',
        commission_rate: partner.commission_rate,
        status: partner.status,
      });
    }
  }, [partner, isEdit, reset]);

  const createMutation = useMutation({
    mutationFn: (data: Partial<Partner>) => partnerService.createPartner(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      alert('제휴사가 등록되었습니다.');
      navigate(`/admin/partners/${data.id}`);
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error?.message || error.message || '등록에 실패했습니다.';
      alert(`등록 실패: ${errorMessage}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Partner>) => partnerService.updatePartner(partnerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      queryClient.invalidateQueries({ queryKey: ['partner', partnerId] });
      alert('제휴사가 수정되었습니다.');
      navigate(`/admin/partners/${partnerId}`);
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error?.message || error.message || '수정에 실패했습니다.';
      alert(`수정 실패: ${errorMessage}`);
    },
  });

  const onSubmit = (data: PartnerFormData) => {
    const formData: Partial<Partner> = {
      partner_name: data.partner_name.trim(),
      contact_name: data.contact_name?.trim() || undefined,
      email: data.email?.trim() || undefined,
      phone: data.phone?.trim() || undefined,
      commission_rate: Number(data.commission_rate),
      status: data.status,
    };

    // 등록 시에만 partner_code 포함
    if (isNew) {
      formData.partner_code = data.partner_code.trim();
    }

    if (isEdit) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  if (isLoading && isEdit) {
    return (
      <AdminLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>{isEdit ? '제휴사 수정' : '제휴사 등록'}</h1>
          <Button variant="secondary" onClick={() => navigate('/admin/partners')}>
            목록으로
          </Button>
        </div>

        <Card>
          <Card.Body>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      제휴사명 <span style={{ color: '#ff6b6b' }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      {...register('partner_name', {
                        required: '제휴사명을 입력해주세요.',
                        minLength: {
                          value: 2,
                          message: '제휴사명은 2자 이상 입력해주세요.',
                        },
                      })}
                      isInvalid={!!errors.partner_name}
                      placeholder="제휴사명을 입력하세요"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.partner_name?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      제휴사 코드 <span style={{ color: '#ff6b6b' }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      {...register('partner_code', {
                        required: isNew ? '제휴사 코드를 입력해주세요.' : false,
                        pattern: {
                          value: /^[A-Z0-9_]+$/,
                          message: '제휴사 코드는 영문 대문자, 숫자, 언더스코어만 사용 가능합니다.',
                        },
                        minLength: {
                          value: 3,
                          message: '제휴사 코드는 3자 이상 입력해주세요.',
                        },
                      })}
                      isInvalid={!!errors.partner_code}
                      disabled={isEdit}
                      placeholder="예: PARTNER001"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.partner_code?.message}
                    </Form.Control.Feedback>
                    {isEdit && (
                      <Form.Text className="text-muted">
                        제휴사 코드는 수정할 수 없습니다.
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>담당자명</Form.Label>
                    <Form.Control
                      type="text"
                      {...register('contact_name')}
                      placeholder="담당자명을 입력하세요"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>이메일</Form.Label>
                    <Form.Control
                      type="email"
                      {...register('email', {
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: '올바른 이메일 형식을 입력해주세요.',
                        },
                      })}
                      isInvalid={!!errors.email}
                      placeholder="email@example.com"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>연락처</Form.Label>
                    <Form.Control
                      type="tel"
                      {...register('phone', {
                        pattern: {
                          value: /^[0-9-]+$/,
                          message: '올바른 연락처 형식을 입력해주세요.',
                        },
                      })}
                      isInvalid={!!errors.phone}
                      placeholder="010-1234-5678"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.phone?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      수수료율 (%) <span style={{ color: '#ff6b6b' }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      {...register('commission_rate', {
                        required: '수수료율을 입력해주세요.',
                        min: {
                          value: 0,
                          message: '수수료율은 0 이상이어야 합니다.',
                        },
                        max: {
                          value: 100,
                          message: '수수료율은 100 이하여야 합니다.',
                        },
                        valueAsNumber: true,
                      })}
                      isInvalid={!!errors.commission_rate}
                      placeholder="0.00"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.commission_rate?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      상태 <span style={{ color: '#ff6b6b' }}>*</span>
                    </Form.Label>
                    <Form.Select
                      {...register('status', { required: '상태를 선택해주세요.' })}
                      isInvalid={!!errors.status}
                    >
                      <option value="active">활성</option>
                      <option value="inactive">비활성</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.status?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button
                  variant="secondary"
                  onClick={() => navigate('/admin/partners')}
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  취소
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? '저장 중...'
                    : isEdit
                    ? '수정'
                    : '등록'}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default PartnerForm;

